import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, errText } from "../lib/api";
import { t } from "../i18n";
import DeliveryProofCard from "../components/DeliveryProofCard";
import { LiveMap } from "../components/DeliveryMap";
import { StarRating } from "../components/StarRating";
import { fmtDate } from "../components/DateField";
import Seo from "../components/Seo";

const PROOF_PHOTO = "https://images.unsplash.com/photo-1533793241176-a270e75ef2ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Track() {
  const { locale = "en" } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(params.get("order") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [issue, setIssue] = useState("");
  const [sent, setSent] = useState(false);
  const [mapsKey, setMapsKey] = useState("");
  const [ratingDone, setRatingDone] = useState(false);

  const reload = () => api.get(`/track/${order.trim().toUpperCase()}`, { params: { email: email.trim(), locale } })
    .then((r) => setData(r.data)).catch(() => {});

  const rate = async (stars) => {
    try { await api.post("/track/rate", { order_number: order.toUpperCase(), email: email.trim(), stars }); setRatingDone(true); reload(); }
    catch (err) { setError(errText(err)); }
  };

  const load = async (e) => {
    e?.preventDefault();
    setError("");
    try {
      const r = await api.get(`/track/${order.trim().toUpperCase()}`, { params: { email: email.trim(), locale } });
      setData(r.data);
    } catch (err) {
      setError(errText(err));
      setData(null);
    }
  };

  useEffect(() => {
    if (params.get("order") && params.get("email")) load();
    api.get("/bootstrap").then((r) => setMapsKey(r.data?.maps?.google_maps_api_key || "")).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live refresh while the courier is on the way.
  useEffect(() => {
    if (data?.status !== "out_for_delivery") return;
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status]);

  const sendIssue = async () => {
    await api.post("/support/request", { order_number: order.toUpperCase(), email, message: issue });
    setSent(true);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="track-page">
      <Seo title={`${t(locale, "track")} | Fleurinelle`} noindex />
      <h1 className="font-display text-3xl lg:text-4xl text-ink">{t(locale, "track")}</h1>
      <form onSubmit={load} className="mt-8 grid sm:grid-cols-3 gap-3">
        <input data-testid="track-order-input" className="field" placeholder={t(locale, "orderNumber")} value={order} onChange={(e) => setOrder(e.target.value)} />
        <input data-testid="track-email-input" className="field" placeholder={t(locale, "buyerEmail")} value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="track-submit" className="btn-primary" type="submit">{t(locale, "trackNow")}</button>
      </form>
      {error ? <p className="text-danger text-sm mt-4" data-testid="track-error">{error}</p> : null}

      {!data ? (
        <section className="mt-14 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-testid="track-proof-preview">
          <div>
            <h2 className="font-display text-2xl lg:text-3xl text-ink">{t(locale, "proofTitle")}</h2>
            <p className="mt-4 text-sm text-muted max-w-lg leading-relaxed">{t(locale, "proofBody")}</p>
          </div>
          <DeliveryProofCard
            locale={locale}
            photo={PROOF_PHOTO}
            recipient="Fatima"
            city="Tangier"
            deliveredAt={new Date().toISOString()}
            shop="Fleurinelle"
            testid="track-delivery-proof-preview"
          />
        </section>
      ) : null}

      {data ? (
        <div className="mt-12 space-y-10" data-testid="track-result">
          <div className="card p-6">
            <p className="smallcaps">{data.order_number}</p>
            <h2 className="font-display text-2xl text-ink mt-2" data-testid="track-status">{data.status_label}</h2>
            <p className="text-sm text-muted mt-1">
              {data.city} · {fmtDate(data.delivery_date)} · {data.delivery_window}
            </p>
            <p className="text-sm text-charcoal mt-3">{t(locale, "arrangedBy")}: Fleurinelle</p>
          </div>

          {data.status === "out_for_delivery" && data.driver_live ? (
            <div className="space-y-2" data-testid="track-live">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brass opacity-70"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brass"></span></span>
                <h3 className="smallcaps">{data.driver_name ? t(locale, "driverOnWay").replace("{name}", data.driver_name) : t(locale, "courierOnWay")}</h3>
              </div>
              {data.eta_minutes ? (
                <p className="text-sm text-ink" data-testid="track-eta">{t(locale, "etaIn")} <b>~{data.eta_minutes} min</b></p>
              ) : null}
              <LiveMap apiKey={mapsKey} driver={data.driver_live} destination={data.delivery_location} testid="track-live-map" />
              {!mapsKey ? <p className="text-xs text-muted">{t(locale, "liveNoMap")}</p> : null}
            </div>
          ) : null}

          <ol className="space-y-4">
            {data.timeline?.map((e, i) => (
              <li key={`${e.status}-${e.at}-${i}`} className="border-s-2 border-brass ps-4" data-testid={`timeline-${e.status}`}>
                <p className="text-sm text-ink">{e.label}</p>
                <p className="text-xs text-muted tabular">{new Date(e.at).toLocaleString(locale === "ar" ? "ar-MA" : locale)}</p>
                {e.note ? <p className="text-xs text-muted">{e.note}</p> : null}
              </li>
            ))}
          </ol>

          {data.proof ? (
            <DeliveryProofCard
              locale={locale}
              photo={data.proof.photo_urls[0]}
              recipient={data.recipient_first_name}
              city={data.city}
              deliveredAt={data.proof.delivered_at}
              shop={data.proof.shop_name}
              testid="track-delivery-proof"
            />
          ) : null}

          {data.driver_rating ? (
            <div className="card p-4 text-center" data-testid="track-rated">
              <p className="text-sm text-ink">{t(locale, "ratedThanks")} {"★".repeat(data.driver_rating.stars)}</p>
            </div>
          ) : data.can_rate ? (
            <div className="card p-5 text-center space-y-3" data-testid="track-rate">
              <h3 className="font-display text-xl text-ink">{t(locale, "howWasDelivery")}</h3>
              {ratingDone ? <p className="text-sm text-success">{t(locale, "feedbackThanks")}</p> : (
                <div className="flex justify-center"><StarRating onRate={rate} testid="track-stars" /></div>
              )}
            </div>
          ) : null}

          <div className="border-t border-brass-light pt-6">
            <h3 className="smallcaps mb-2">{t(locale, "somethingWrong")}</h3>
            {sent ? (
              <p className="text-sm text-success" data-testid="issue-sent">✓</p>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <input data-testid="issue-input" className="field" value={issue} onChange={(e) => setIssue(e.target.value)} />
                <button data-testid="issue-submit" className="btn-ghost" onClick={sendIssue} disabled={!issue}>
                  {t(locale, "support")}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
